import { useState } from "react";
import { Check, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CsvButton } from "./salesUi";

function Reassign({ client, onDone }) {
  const { user } = useAuth();
  const { data: accounts } = useFetch("/sales/accounts");
  const [f, setF] = useState({ to_agent_id: "", reason: "" });
  const [error, setError] = useState("");
  const options = (accounts || []).filter((a) => a.active && a.approval_status === "APPROVED" && a.role !== "cashier" && a.id !== client.seller_id);
  if (user.role === "sales_manager" && client.seller_id !== user.id) options.push({ id: user.id, full_name: `${user.full_name} (me)` });
  const submit = async () => {
    try { await api.post(`/sales/clients/${client.id}/reassign`, f); toast.success("Reassignment requested — awaiting admin approval"); onDone(); } catch (e) { setError(errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid="reassign-form">
      <p className="label">Request reassignment (admin approval required)</p>
      <select className="field" value={f.to_agent_id} onChange={(e) => setF({ ...f, to_agent_id: e.target.value })} data-testid="reassign-to-select">
        <option value="">Move to…</option>{options.map((a) => <option key={a.id} value={a.id}>{a.full_name}</option>)}
      </select>
      <input className="field" placeholder="Reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="reassign-reason-input" />
      <ErrorBox error={error} />
      <button className="btn-secondary btn-sm" disabled={!f.to_agent_id || f.reason.length < 5} onClick={submit} data-testid="reassign-submit">Submit request</button>
    </div>
  );
}

function ClientDetail({ id, onClose }) {
  const { user } = useAuth();
  const { data: c, reload } = useFetch(`/sales/clients/${id}`);
  const lead = ["admin", "super_admin", "sales_manager"].includes(user.role);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl" data-testid="client-detail-dialog">
        <DialogHeader><DialogTitle>{c?.name || "Client"}</DialogTitle></DialogHeader>
        {!c ? <Spinner /> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <p>Client ID<br /><b className="font-mono">{c.client_no}</b></p><p>Agent<br /><b>{c.seller_name}</b></p>
              <p>Email<br /><b data-testid="client-detail-email">{c.email}</b></p><p>Mobile<br /><b>{c.mobile}</b></p>
              <p>Package<br /><b>{c.package_name || "—"}</b></p><p>Card<br /><b className="font-mono">{c.card_serial || "—"}</b></p>
              <p>Membership<br />{c.membership_status ? <StatusBadge status={c.membership_status} /> : "—"}</p><p>Expires<br /><b>{fmtDate(c.expires_at)}</b></p>
              <p>Registered<br /><b>{c.card_status === "REGISTERED" ? "Yes" : "Not yet"}</b></p><p>Payment<br />{c.payment_status ? <StatusBadge status={c.payment_status} /> : "—"}</p>
            </div>
            {c.flags?.includes("DUPLICATE_ACTIVE") && <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800" data-testid="client-duplicate-flag">Flagged: this email or mobile already had an active membership.</p>}
            <div><p className="label">Transactions</p>{c.transactions.map((t) => (
              <p key={t.id} className="flex justify-between border-b border-slate-100 py-1.5 text-xs"><span className="font-mono">{t.reference}</span><span>{money(t.amount)} · <StatusBadge status={t.status} /></span></p>
            ))}</div>
            {c.reassignments.length > 0 && <div><p className="label">Reassignments</p>{c.reassignments.map((r) => <p key={r.id} className="text-xs">{r.to_name} · {r.status} · {r.reason}</p>)}</div>}
            {lead && !c.reassignments.some((r) => r.status === "PENDING") && <Reassign client={c} onDone={reload} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function Reassignments({ onDecided }) {
  const { data, reload } = useFetch("/sales/reassignments?status=PENDING");
  const decide = async (id, decision) => {
    try { await api.post(`/sales/reassignments/${id}/decision`, { decision }); toast.success(`Request ${decision.toLowerCase()}`); reload(); onDecided?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data?.length) return null;
  return (
    <div className="surface p-4" data-testid="reassign-requests">
      <p className="mb-2 text-sm font-bold">Pending client reassignments</p>
      {data.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2 text-xs">
          <span><b>{r.client_name}</b> → {r.to_name} · “{r.reason}” · by {r.requested_by_name} · {fmtDateTime(r.created_at)}</span>
          <span className="flex gap-1">
            <button className="btn-primary btn-sm" onClick={() => decide(r.id, "APPROVED")} data-testid={`reassign-approve-${r.id}`}><Check size={13} /> Approve</button>
            <button className="btn-secondary btn-sm" onClick={() => decide(r.id, "REJECTED")} data-testid={`reassign-reject-${r.id}`}><X size={13} /> Reject</button>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function Clients() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const { data, loading, reload } = useFetch(`/sales/clients${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  const rows = (data || []).map((c) => ({ client_id: c.client_no, name: c.name, email: c.email, mobile: c.mobile, package: c.package_name, card: c.card_serial,
    agent: c.seller_name, payment: c.payment_status, membership: c.membership_status, expires: c.expires_at, created: c.created_at }));
  return (
    <div className="space-y-4">
      <PageHead eyebrow="Sales Force" title="Clients" sub="Contact details are partly hidden in lists. Open a client to see full details." actions={<CsvButton name="giacare-clients.csv" rows={rows} />} />
      {["admin", "super_admin"].includes(user.role) && <Reassignments onDecided={reload} />}
      <div className="relative"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="field pl-9" placeholder="Search name, client ID or card serial" value={q} onChange={(e) => setQ(e.target.value)} data-testid="client-search-input" /></div>
      {loading && !data ? <Spinner /> : !data?.length ? <Empty title="No clients yet" hint="Clients appear after you start a sale." /> : (
        <div className="surface overflow-x-auto">
          <table className="data-table" data-testid="clients-table">
            <thead><tr><th>Client</th><th>Contact</th><th>Card</th><th>Agent</th><th>Payment</th><th>Membership</th><th>Expires</th></tr></thead>
            <tbody>{data.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={() => setOpen(c.id)} data-testid={`client-row-${c.client_no}`}>
                <td><b>{c.name}</b><br /><span className="font-mono text-[10px] text-slate-400">{c.client_no}</span></td>
                <td className="text-xs">{c.email}<br />{c.mobile}</td><td className="font-mono text-xs">{c.card_serial || "—"}</td><td className="text-xs">{c.seller_name}</td>
                <td>{c.payment_status ? <StatusBadge status={c.payment_status} /> : "—"}</td><td>{c.membership_status ? <StatusBadge status={c.membership_status} /> : "—"}</td>
                <td className="text-xs">{fmtDate(c.expires_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {open && <ClientDetail id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
